/**
 * Negamax search with alpha-beta pruning and a piece-square evaluation.
 * Depth is chosen by difficulty; the search runs synchronously between
 * animations, so depths are kept modest enough to stay responsive.
 */

import { fileOf, rankOf, other } from './chess.js';

const VALUE = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 20000 };

// Piece-square tables written from White's perspective, rank 8 first.
const PST = {
  p: [
     0,  0,  0,  0,  0,  0,  0,  0,
    50, 50, 50, 50, 50, 50, 50, 50,
    10, 10, 20, 30, 30, 20, 10, 10,
     5,  5, 10, 25, 25, 10,  5,  5,
     0,  0,  0, 20, 20,  0,  0,  0,
     5, -5,-10,  0,  0,-10, -5,  5,
     5, 10, 10,-20,-20, 10, 10,  5,
     0,  0,  0,  0,  0,  0,  0,  0
  ],
  n: [
   -50,-40,-30,-30,-30,-30,-40,-50,
   -40,-20,  0,  0,  0,  0,-20,-40,
   -30,  0, 10, 15, 15, 10,  0,-30,
   -30,  5, 15, 20, 20, 15,  5,-30,
   -30,  0, 15, 20, 20, 15,  0,-30,
   -30,  5, 10, 15, 15, 10,  5,-30,
   -40,-20,  0,  5,  5,  0,-20,-40,
   -50,-40,-30,-30,-30,-30,-40,-50
  ],
  b: [
   -20,-10,-10,-10,-10,-10,-10,-20,
   -10,  0,  0,  0,  0,  0,  0,-10,
   -10,  0,  5, 10, 10,  5,  0,-10,
   -10,  5,  5, 10, 10,  5,  5,-10,
   -10,  0, 10, 10, 10, 10,  0,-10,
   -10, 10, 10, 10, 10, 10, 10,-10,
   -10,  5,  0,  0,  0,  0,  5,-10,
   -20,-10,-10,-10,-10,-10,-10,-20
  ],
  r: [
     0,  0,  0,  0,  0,  0,  0,  0,
     5, 10, 10, 10, 10, 10, 10,  5,
    -5,  0,  0,  0,  0,  0,  0, -5,
    -5,  0,  0,  0,  0,  0,  0, -5,
    -5,  0,  0,  0,  0,  0,  0, -5,
    -5,  0,  0,  0,  0,  0,  0, -5,
    -5,  0,  0,  0,  0,  0,  0, -5,
     0,  0,  0,  5,  5,  0,  0,  0
  ],
  q: [
   -20,-10,-10, -5, -5,-10,-10,-20,
   -10,  0,  0,  0,  0,  0,  0,-10,
   -10,  0,  5,  5,  5,  5,  0,-10,
    -5,  0,  5,  5,  5,  5,  0, -5,
     0,  0,  5,  5,  5,  5,  0, -5,
   -10,  5,  5,  5,  5,  5,  0,-10,
   -10,  0,  5,  0,  0,  0,  0,-10,
   -20,-10,-10, -5, -5,-10,-10,-20
  ],
  k: [
   -30,-40,-40,-50,-50,-40,-40,-30,
   -30,-40,-40,-50,-50,-40,-40,-30,
   -30,-40,-40,-50,-50,-40,-40,-30,
   -30,-40,-40,-50,-50,-40,-40,-30,
   -20,-30,-30,-40,-40,-30,-30,-20,
   -10,-20,-20,-20,-20,-20,-20,-10,
    20, 20,  0,  0,  0, 20, 20, 20,
    20, 30, 10,  0,  0, 10, 30, 20
  ]
};

export const DIFFICULTIES = {
  squire: { label: 'Squire', depth: 1, blunder: 0.35 },
  manatarms: { label: 'Man-at-Arms', depth: 2, blunder: 0.20 },
  knight: { label: 'Knight', depth: 3, blunder: 0.12 },
  warlord: { label: 'Warlord', depth: 4, blunder: 0 }
};

function evaluate(game, color) {
  let score = 0;
  for (let i = 0; i < 64; i++) {
    const piece = game.board[i];
    if (!piece) continue;
    // Mirror the table vertically for Black.
    const table = PST[piece.type];
    const pst = piece.color === 'w' ? table[i] : table[(7 - Math.floor(i / 8)) * 8 + (i % 8)];
    const value = VALUE[piece.type] + pst;
    score += piece.color === color ? value : -value;
  }
  return score;
}

/** Most-valuable-victim / least-valuable-attacker ordering for better pruning. */
function orderMoves(moves) {
  return moves
    .map((m) => ({
      m,
      score:
        (m.captured ? VALUE[m.captured.type] * 10 - VALUE[m.piece.type] : 0) +
        (m.promotion ? VALUE[m.promotion] : 0)
    }))
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.m);
}

function negamax(game, depth, alpha, beta, color) {
  const moves = game.legalMoves(color);
  if (!moves.length) {
    return game.inCheck(color) ? -100000 - depth : 0;
  }
  if (depth === 0) return evaluate(game, color);

  let best = -Infinity;
  for (const move of orderMoves(moves)) {
    const undo = game.applyMove(move);
    const score = -negamax(game, depth - 1, -beta, -alpha, other(color));
    game.undoMove(undo);
    if (score > best) best = score;
    if (best > alpha) alpha = best;
    if (alpha >= beta) break; // Opponent has a better option elsewhere.
  }
  return best;
}

/** Picks a move for the side to move. Returns null when the game is over. */
export function chooseMove(game, difficulty = 'knight') {
  const config = DIFFICULTIES[difficulty] ?? DIFFICULTIES.knight;
  const color = game.turn;
  const search = game.clone();
  const moves = search.legalMoves(color);
  if (!moves.length) return null;

  const scored = [];
  let alpha = -Infinity;
  for (const move of orderMoves(moves)) {
    const undo = search.applyMove(move);
    const score = -negamax(search, config.depth - 1, -Infinity, -alpha, other(color));
    search.undoMove(undo);
    scored.push({ move, score });
    if (score > alpha) alpha = score;
  }

  scored.sort((a, b) => b.score - a.score);

  // Weaker opponents sometimes settle for a merely decent move.
  if (config.blunder && Math.random() < config.blunder && scored.length > 1) {
    const pool = scored.slice(0, Math.min(4, scored.length));
    const pick = pool[Math.floor(Math.random() * pool.length)];
    return matchMove(game, pick.move);
  }

  // Break ties randomly so openings vary between games.
  const best = scored.filter((s) => s.score === scored[0].score);
  return matchMove(game, best[Math.floor(Math.random() * best.length)].move);
}

/** Maps a move found on the cloned board back onto the live game's move list. */
function matchMove(game, move) {
  return game.legalMoves().find(
    (m) => m.from === move.from && m.to === move.to && m.promotion === move.promotion
  ) ?? null;
}
